import { useCallback, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Radio, RadioGroup, Spinner } from '@ds';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { DEVICE_REMOVAL_TEXT, RESOURCE_REMOVE_TEXT, RESOURCE_SWAP_TEXT } from '@lp/features/RoutePlanner/config';
import { OcsConsoleData } from '@/api/apis/resources';
import { CaseOCSConsoleResource, CaseResource, OrganType } from '@/api/apis/case';
import { resourceLogisticsStatusNames } from '@/constants/resources.constants';
import { RoutePlannerDeviceList } from './DeviceListTable/DeviceList';
import { ActiveGroundTripWarning } from './ActiveGroundTripWarning';

export type RemoveDeviceModalProps = {
  title: string;
  resource?: CaseResource<CaseOCSConsoleResource> | null;
  organType: OrganType;
  isOpen: boolean;
  isLoading: boolean;
  onRemove: (removeType?: 'remove' | 'swap', newResourceId?: string) => void;
  onClose: () => void;
};

export const RemoveDeviceModal = ({
  title,
  resource,
  isOpen,
  isLoading,
  organType,
  onRemove,
  onClose,
}: RemoveDeviceModalProps) => {
  const [removeType, setRemoveType] = useState<'remove' | 'swap' | null>(null);
  const [newResourceId, setNewResourceId] = useState<string>('');

  const handleSelectNewResource = useCallback((device: OcsConsoleData) => {
    setNewResourceId(device.id);
  }, []);

  const handleResourceRemove = () => {
    if (removeType) {
      onRemove(removeType, newResourceId);
    }
  };

  const onTabChange = (value: string) => {
    setRemoveType(value as 'remove' | 'swap');
  };

  const modalSize = removeType === 'swap' ? 'lg' : 'md';

  return (
    <Modal
      size={modalSize}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={45} style={{ top: 15 }} />}
      classNames={{
        base: `px-6 overflow-scroll max-h-[85svh] ${removeType === 'swap' ? 'w-auto max-w-fit' : ''}`,
        body: 'border-y-1 border-base-30 px-0 py-6 text-l-r overflow-auto',
        header: 'px-0 font-secondary text-hl! text-base-20',
        footer: 'px-0 flex gap-6',
        wrapper: 'z-420',
        backdrop: 'z-419',
      }}
    >
      <ModalContent>
        {() => (
          <>
            {isLoading && (
              <div className="absolute top-0 left-0 flex justify-center items-center z-50 w-full h-full bg-white bg-opacity-40">
                <Spinner size="md" />
              </div>
            )}
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
              <TmdxResourceCard
                label="Actions"
                list={[]}
                type="device"
                title={(resource!.data! as OcsConsoleData).ocsConsoleName}
                subTitle={(resource!.data! as OcsConsoleData).fullSerialNumber}
                phoneNumber=""
                isReadOnly
                menuDisabled
                chipProps={{
                  status: resource!.logisticsStatus,
                }}
                chipText={resourceLogisticsStatusNames[resource!.logisticsStatus]}
                avatarProps={{
                  type: 'device',
                  organ: organType,
                  src: (resource!.data! as OcsConsoleData).logoPath,
                }}
                style={{ overflow: 'initial' }}
              />
              <div className="flex flex-col gap-4 pt-6 pb-3">
                <ActiveGroundTripWarning resourceId={resource?.resourceId} />
                <span className="text-m-r text-base-5">{DEVICE_REMOVAL_TEXT}</span>
                <RadioGroup className="gap-2 p-0" value={removeType} onValueChange={onTabChange}>
                  <Radio size="md" value="remove">
                    {RESOURCE_REMOVE_TEXT}
                  </Radio>
                  <Radio size="md" value="swap">
                    {RESOURCE_SWAP_TEXT}
                  </Radio>
                </RadioGroup>
                {removeType === 'swap' && (
                  <>
                    <ModalHeader className="text-l! text-hm! p-0 m-0 text-base-5">
                      Select resource to swap to
                    </ModalHeader>
                    <RoutePlannerDeviceList
                      organType={organType}
                      selectedHubId={resource?.data?.nopHub}
                      selectedResourceId={newResourceId}
                      onSelect={handleSelectNewResource}
                      isSwap
                    />
                  </>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button isDisabled={isLoading} size="md" color="default" variant="outline" onPress={onClose}>
                Cancel
              </Button>
              <Button isDisabled={!removeType || isLoading} size="md" color="default" onPress={handleResourceRemove}>
                {removeType === 'swap' ? 'Remove and swap' : 'Remove'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
